import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AcademyActivationCredentials from '../components/academy/AcademyActivationCredentials';
import { useUserRole } from '../context/UserRoleContext';
import {
  acceptAcademyInvitation,
  registerAcademyInvitee,
  signInAcademyInvitee
} from '../services/academyInvitationActivationService';
import { getCurrentUserEnrollmentForCourse } from '../services/academyEnrollmentService';

const TOKEN_STORAGE_KEY = 'academy-invitation-token';
const COURSE_KEY = 'psykososialt-arbeidsmiljo-praktisk-handling';

function readInviteToken(routeSearch) {
  const routeToken = new URLSearchParams(routeSearch).get('token');
  const callbackParams = new URLSearchParams(window.location.search);
  const callbackToken = callbackParams.get('academy_invite') || callbackParams.get('token');

  if (routeToken || callbackToken) {
    const token = routeToken || callbackToken;
    try {
      window.sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch {
      // The token remains in component state if session storage is unavailable.
    }
    return token;
  }

  try {
    return window.sessionStorage.getItem(TOKEN_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function removeInviteTokenFromUrl() {
  const callbackParams = new URLSearchParams(window.location.search);
  callbackParams.delete('academy_invite');
  callbackParams.delete('token');
  const callbackSearch = callbackParams.toString();
  const route = window.location.hash.split('?')[0] || '#/academy/activate';
  const cleanUrl = `${window.location.pathname}${callbackSearch ? `?${callbackSearch}` : ''}${route}`;
  window.history.replaceState(window.history.state, '', cleanUrl);
}

function confirmed(user) {
  return Boolean(user?.email_confirmed_at || user?.confirmed_at);
}

export default function AcademyActivate() {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, authUser, loading: authLoading, refreshProfile, signOut } = useUserRole();
  const [token, setToken] = useState(() => readInviteToken(location.search));
  const [mode, setMode] = useState('register');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [activated, setActivated] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const [syncWarning, setSyncWarning] = useState('');

  useEffect(() => {
    const nextToken = readInviteToken(location.search);
    if (nextToken) {
      setToken(nextToken);
      removeInviteTokenFromUrl();
    }
  }, [location.search]);

  const user = authUser || session?.user;
  const isSignedIn = Boolean(session?.user || authUser);
  const isEmailConfirmed = confirmed(user);

  async function handleRegister(credentials) {
    setError('');
    setNotice('');
    if (credentials.validationError) {
      setError(credentials.validationError);
      return;
    }
    setBusy(true);
    try {
      const result = await registerAcademyInvitee(
        credentials.email,
        credentials.password,
        token
      );
      if (result.confirmationRequired) {
        setNotice('Kontoen er opprettet. Åpne bekreftelseslenken vi har sendt til e-posten din. Du kommer tilbake hit for å aktivere kurset.');
      } else {
        setNotice('Kontoen er opprettet. Bekreft e-postadressen før du aktiverer kurset.');
      }
    } catch (failure) {
      setError(failure.message || 'Registreringen kunne ikke fullføres.');
    } finally {
      setBusy(false);
    }
  }

  async function handleLogin(credentials) {
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await signInAcademyInvitee(credentials.email, credentials.password);
      setNotice('Du er logget inn. Du kan aktivere kurset når e-postadressen er bekreftet.');
    } catch (failure) {
      setError(failure.message || 'Innloggingen kunne ikke fullføres.');
    } finally {
      setBusy(false);
    }
  }

  async function handleActivate() {
    if (!token || !isSignedIn || !isEmailConfirmed || busy) return;
    setBusy(true);
    setError('');
    setNotice('');

    try {
      await acceptAcademyInvitation(token);
      setActivated(true);
      setToken('');
      setProfileReady(false);
      try {
        window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      } catch {
        // Session storage is optional; the URL has already been cleaned.
      }

      try {
        await refreshProfile();
        await getCurrentUserEnrollmentForCourse({
          courseKey: COURSE_KEY,
          version: '1.0',
          languageCode: 'nb',
          includeCompleted: true
        });
        setProfileReady(true);
      } catch {
        setSyncWarning('Aktiveringen er lagret. Profilen eller kursdataene kunne ikke oppdateres automatisk; logg inn på nytt eller last siden på nytt.');
      }
    } catch (failure) {
      setError(failure.message || 'Invitasjonen kunne ikke aktiveres.');
    } finally {
      setBusy(false);
    }
  }

  async function handleSignOut() {
    setError('');
    try {
      await signOut();
      setNotice('Du er logget ut. Logg inn med e-postadressen som mottok invitasjonen.');
    } catch {
      setError('Utloggingen kunne ikke fullføres. Prøv igjen.');
    }
  }

  function openCourse() {
    navigate('/dashboard/courses');
  }

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-12 sm:px-6">
      <div className="mx-auto grid min-h-[75vh] max-w-5xl items-center gap-10 lg:grid-cols-[1fr_0.9fr]">
        <section className="space-y-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">HMS-innsikt Academy</p>
          <h1 className="max-w-xl text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">
            Velkommen til HMS Innsikt Academy
          </h1>
          <p className="max-w-xl text-lg leading-8 text-slate-600">
            Du er invitert til et kurs. Opprett en konto eller logg inn med den bekreftede e-postadressen som mottok invitasjonen, og aktiver kurset når du er klar.
          </p>
          <div className="rounded-2xl border border-blue-100 bg-white p-5 text-sm leading-6 text-slate-600 shadow-sm">
            Invitasjonen kobles til kontoen din først når du velger «Aktiver kurset mitt». Kurs- og deltakerinformasjon vises ikke på denne siden før tilgangen er bekreftet.
          </div>
        </section>

        <section className="space-y-4">
          {!token && !activated && (
            <p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
              Invitasjonstokenet mangler. Åpne aktiveringslenken du fikk fra kursadministratoren.
            </p>
          )}

          {activated ? (
            <div className="rounded-2xl border border-emerald-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-bold text-emerald-900">Kurset er aktivert</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Påmeldingen er bekreftet. Academy-profil og kursdata ble hentet på nytt fra Supabase.
              </p>
              {syncWarning && <p role="alert" className="mt-4 rounded-xl bg-amber-50 p-4 text-sm leading-6 text-amber-900">{syncWarning}</p>}
              {profileReady && (
                <button
                  type="button"
                  onClick={openCourse}
                  className="mt-5 w-full rounded-xl bg-emerald-700 px-4 py-3 font-bold text-white transition hover:bg-emerald-800"
                >
                  Åpne kurset mitt
                </button>
              )}
            </div>
          ) : isSignedIn && isEmailConfirmed ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-sm text-slate-600">Innlogget som <strong className="text-slate-900">{user?.email}</strong></p>
              <button
                type="button"
                onClick={handleActivate}
                disabled={!token || busy || authLoading}
                className="mt-5 w-full rounded-xl bg-blue-700 px-4 py-3 font-bold text-white transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? 'Aktiverer …' : 'Aktiver kurset mitt'}
              </button>
              <button type="button" onClick={handleSignOut} className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                Logg ut og bruk en annen konto
              </button>
              {error && <p role="alert" className="mt-4 rounded-xl bg-rose-50 p-4 text-sm leading-6 text-rose-800">{error}</p>}
            </div>
          ) : isSignedIn ? (
            <div className="rounded-2xl border border-amber-200 bg-white p-6 shadow-sm">
              <h2 className="font-bold text-slate-900">Bekreft e-postadressen din</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Åpne bekreftelseslenken fra Supabase Auth. Den returnerer deg hit med invitasjonskonteksten bevart.
              </p>
              <button type="button" onClick={handleSignOut} className="mt-4 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                Logg ut
              </button>
            </div>
          ) : token ? (
            <AcademyActivationCredentials
              mode={mode}
              onModeChange={(nextMode) => {
                setMode(nextMode);
                setError('');
                setNotice('');
              }}
              onLogin={handleLogin}
              onRegister={handleRegister}
              busy={busy}
              notice={notice}
              error={error}
            />
          ) : null}
        </section>
      </div>
    </main>
  );
}